import React, {
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { Formik, Form, FieldArray, getIn } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { selectUser } from "../../redux/modules/userSlice";
import { NewJoineeAPI, UserAPI } from "../api";
import useDraft from "../../hooks/useDraft";
import {
  applySectionDraftData,
  pickSectionDraftData,
} from "../../drafts/draftHelpers";
import {
  JOINEE_FORM_COLORS,
  JOINEE_FORM_FONT,
  NATURE_OF_BUSINESS_OPTIONS,
  TYPE_OF_EMPLOYMENT_OPTIONS,
  emptyEmployerRow,
} from "../../config/joineeFormConstants";
import {
  FormSectionTitle,
  FormAddButton,
  FormTextField,
  FormSelectField,
  FormDateField,
  FormTwoCol,
  FormFieldLabel,
} from "./JoineeFormFields";

import { ruleSchema, validateDraft, notifyFormInvalid } from "../../utils/inputRules";

var DRAFT_SECTION = "B";

function errMsg(formik, path) {
  var touched = getIn(formik.touched, path);
  var error = getIn(formik.errors, path);
  return touched && error ? error : "";
}

function normalizeMobile(value) {
  if (!value) {
    return "";
  }
  var digits = String(value).replace(/\D/g, "");
  if (digits.length === 12 && digits.indexOf("91") === 0) {
    return digits.slice(2);
  }
  if (digits.length === 11 && digits.charAt(0) === "0") {
    return digits.slice(1);
  }
  return digits;
}

function formatExperienceLabel(years, months) {
  var y = years ? String(years) : "0";
  var m = months ? String(months) : "0";
  return y + " Year(s), " + m + " Month(s)";
}

function employerSchema() {
  return Yup.object({
    employerName: ruleSchema("orgName", {
      label: "Employer name",
      required: true,
    }),
    companyContact: ruleSchema("mobile", {
      label: "Phone number",
      required: true,
    }),
    department: ruleSchema("text", { label: "Department", required: true }),
    employeeCode: ruleSchema("code", {
      label: "Employee code",
      required: true,
    }),
    designation: ruleSchema("text", { label: "Designation", required: true }),
    remuneration: ruleSchema("remuneration", {
      label: "Remuneration",
      required: true,
    }),
    reportedTo: ruleSchema("personName", {
      label: "Reported person name",
      required: true,
      requiredMessage: "Reported To is required",
    }),
    reportedPersonDesig: ruleSchema("text", {
      label: "Reported person designation",
      required: true,
    }),
    natureOfBusiness: Yup.string()
      .trim()
      .required("Nature of business is required"),
    typeOfEmployment: Yup.string()
      .trim()
      .required("Type of employment is required"),
    addressLine1: ruleSchema("address", {
      label: "Address Line 1",
      required: true,
    }),
    addressLine2: ruleSchema("address", { label: "Address Line 2" }),
    city: ruleSchema("place", { label: "City", required: true }),
    state: ruleSchema("place", { label: "State", required: true }),
    doj: Yup.mixed().nullable().required("Date of Joining is required"),
    dol: Yup.mixed()
      .nullable()
      .required("Date of Leaving is required")
      .test("after-doj", "Leaving date must be after joining date", function (
        val
      ) {
        var doj = this.parent.doj;
        if (!val || !doj) {
          return true;
        }
        return new Date(val).getTime() >= new Date(doj).getTime();
      }),
    reasonForLeaving: Yup.string()
      .trim()
      .required("Reason for leaving is required")
      .max(250, "Reason for leaving must be at most 250 characters"),
    referenceContactPerson1Name: ruleSchema("personName", {
      label: "Reference name",
      required: true,
    }),
    referenceContactPerson1Contact: ruleSchema("mobile", {
      label: "Reference phone",
      required: true,
    }),
    referenceContactPerson2Name: ruleSchema("personName", {
      label: "Reference name",
      required: true,
    }),
    referenceContactPerson2Contact: ruleSchema("mobile", {
      label: "Reference phone",
      required: true,
    }),
  });
}

function buildValidationSchema() {
  return Yup.object({
    employmentHistory: Yup.array().of(employerSchema()).min(1),
  });
}

function setBusy(onBusyChange, value) {
  if (typeof onBusyChange === "function") {
    onBusyChange(value);
  }
}

function ReadOnlyBox(props) {
  return (
    <Box sx={{ width: "100%" }}>
      <FormFieldLabel>{props.label}</FormFieldLabel>
      <Box
        sx={{
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          padding: "10px",
          minHeight: "36px",
          height: "36px",
          bgcolor: "#F5F6F7",
          border: "1px solid #C7CCD1",
          borderRadius: "6px",
        }}
      >
        <Typography
          sx={{
            fontFamily: JOINEE_FORM_FONT,
            fontSize: 14,
            fontWeight: 400,
            color: "#171A1C",
            lineHeight: 1.14,
          }}
        >
          {props.value || "—"}
        </Typography>
      </Box>
      <Box sx={{ minHeight: 18 }} />
    </Box>
  );
}

function EmploymentHistory(props) {
  var formikRef = props.formikRef;
  var changeTab = props.changeTab;
  var handleButtonClick = props.handleButtonClick;
  var onBusyChange = props.onBusyChange;
  var hideInternalActions = Boolean(props.hideInternalActions);
  var readOnly = Boolean(props.readOnly);

  var reduxUser = useSelector(selectUser);
  var params = useParams();
  var id = params.id ? params.id : "";
  var user = reduxUser && reduxUser.user ? reduxUser.user : {};
  var isAdmin = Boolean(user.isAdmin);
  var userId = isAdmin ? id || props.id : user._id;

  var draftB = useDraft("newJoinee", DRAFT_SECTION);
  var draftA = useDraft("newJoinee", "A");
  var loadDraftB = draftB.loadDraft;
  var saveDraftB = draftB.saveDraft;
  var deleteDraftB = draftB.deleteDraft;
  var loadDraftA = draftA.loadDraft;

  var [initialValues, setInitialValues] = useState({
    employmentHistory: [emptyEmployerRow(userId)],
  });
  var [formKey, setFormKey] = useState(0);
  var [loading, setLoading] = useState(true);
  var [summary, setSummary] = useState({
    hasPriorExperience: true,
    totalExperienceInYears: "",
    totalExperienceInMonths: "",
    relevantExperienceInYears: "",
    relevantExperienceInMonths: "",
  });
  var formikInstanceRef = useRef(null);
  var deletedIdsRef = useRef([]);

  useEffect(
    function () {
      var isActive = true;

      function finish(rows, summaryNext) {
        if (!isActive) {
          return;
        }
        setSummary(summaryNext);
        setInitialValues({
          employmentHistory:
            rows && rows.length ? rows : [emptyEmployerRow(userId)],
        });
        setFormKey(function (k) {
          return k + 1;
        });
        setLoading(false);
      }

      setLoading(true);
      deletedIdsRef.current = [];

      function mapSummaryFromUser(userData, draftUser) {
        var src = draftUser || userData || {};
        // Existing API: isFresher === true means has prior experience (legacy naming).
        var hasPrior =
          typeof src.hasPriorExperience === "boolean"
            ? src.hasPriorExperience
            : typeof src.isFresher === "boolean"
              ? src.isFresher
              : true;
        return {
          hasPriorExperience: hasPrior,
          totalExperienceInYears: src.totalExperienceInYears
            ? String(src.totalExperienceInYears)
            : "",
          totalExperienceInMonths: src.totalExperienceInMonths
            ? String(src.totalExperienceInMonths)
            : "",
          relevantExperienceInYears: src.relevantExperienceInYears
            ? String(src.relevantExperienceInYears)
            : "",
          relevantExperienceInMonths: src.relevantExperienceInMonths
            ? String(src.relevantExperienceInMonths)
            : "",
        };
      }

      function loadApi() {
        return Promise.all([
          NewJoineeAPI.getEmploymentHistory(userId),
          UserAPI.getUser(userId),
        ])
          .then(function (results) {
            if (!isActive) {
              return;
            }
            var empRes = results[0] || {};
            var userRes = results[1] || {};
            var userData = userRes.data || empRes.sectionAData || {};
            var summaryNext = mapSummaryFromUser(userData, null);
            var rows =
              empRes.data && empRes.data.length
                ? empRes.data.map(function (row) {
                    return Object.assign({}, emptyEmployerRow(userId), row, {
                      userId: userId,
                    });
                  })
                : [emptyEmployerRow(userId)];

            if (!summaryNext.hasPriorExperience) {
              finish([emptyEmployerRow(userId)], summaryNext);
              return;
            }
            finish(rows, summaryNext);
          })
          .catch(function (err) {
            console.log(err);
            if (isActive) {
              setLoading(false);
              toast.error("Unable to load employment history");
            }
          });
      }

      if (!isAdmin) {
        Promise.all([loadDraftA(), loadDraftB()])
          .then(function (drafts) {
            if (!isActive) {
              return;
            }
            var aRaw = drafts[0] && drafts[0].data ? drafts[0].data : drafts[0];
            var bRaw = drafts[1] && drafts[1].data ? drafts[1].data : drafts[1];
            var draftUser =
              aRaw && aRaw.user ? aRaw.user : aRaw && aRaw._id ? aRaw : null;

            return NewJoineeAPI.getEmploymentHistory(userId)
              .then(function (empRes) {
                return UserAPI.getUser(userId).then(function (userRes) {
                  return { empRes: empRes, userRes: userRes, draftUser: draftUser, bRaw: bRaw };
                });
              })
              .then(function (ctx) {
                if (!isActive) {
                  return;
                }
                var userData =
                  (ctx.userRes && ctx.userRes.data) ||
                  (ctx.empRes && ctx.empRes.sectionAData) ||
                  {};
                var summaryNext = mapSummaryFromUser(userData, ctx.draftUser);

                if (!summaryNext.hasPriorExperience) {
                  if (ctx.bRaw) {
                    deleteDraftB();
                  }
                  finish([emptyEmployerRow(userId)], summaryNext);
                  return;
                }

                var applied = applySectionDraftData(
                  "newJoinee",
                  DRAFT_SECTION,
                  { employmentHistory: [] },
                  ctx.bRaw
                );
                var draftRows =
                  applied &&
                  Array.isArray(applied.employmentHistory) &&
                  applied.employmentHistory.length
                    ? applied.employmentHistory
                    : null;
                var apiRows =
                  ctx.empRes && ctx.empRes.data && ctx.empRes.data.length
                    ? ctx.empRes.data
                    : null;
                var rows = (draftRows || apiRows || [emptyEmployerRow(userId)]).map(
                  function (row) {
                    return Object.assign({}, emptyEmployerRow(userId), row, {
                      userId: userId,
                    });
                  }
                );
                finish(rows, summaryNext);
              });
          })
          .catch(function () {
            loadApi();
          });
      } else {
        loadApi();
      }

      return function () {
        isActive = false;
      };
    },
    [userId, isAdmin, loadDraftA, loadDraftB, deleteDraftB]
  );

  function normalizeRow(row) {
    return Object.assign({}, row, {
      userId: userId,
      companyContact: normalizeMobile(row.companyContact),
      referenceContactPerson1Contact: normalizeMobile(
        row.referenceContactPerson1Contact
      ),
      referenceContactPerson2Contact: normalizeMobile(
        row.referenceContactPerson2Contact
      ),
    });
  }

  function handleSaveDraft(formik) {
    if (isAdmin) {
      return Promise.resolve();
    }
    return validateDraft(formik, buildValidationSchema()).then(function (ok) {
      return ok ? persistDraft(formik) : null;
    });
  }

  function persistDraft(formik) {
    var rows = (formik.values.employmentHistory || []).map(normalizeRow);
    var wrapped = pickSectionDraftData("newJoinee", DRAFT_SECTION, {
      employmentHistory: rows,
    });
    setBusy(onBusyChange, "draft");
    return saveDraftB(wrapped)
      .then(function (saved) {
        setBusy(onBusyChange, false);
        if (saved && saved.skipped) {
          toast.info("Nothing to save as draft");
          return saved;
        }
        if (saved) {
          toast.success("Draft saved");
          return saved;
        }
        toast.error("Unable to save draft");
        return null;
      })
      .catch(function () {
        setBusy(onBusyChange, false);
        toast.error("Unable to save draft");
        return null;
      });
  }

  function handleSaveAndNext(values, formikHelpers) {
    var rows = (values.employmentHistory || []).map(normalizeRow);
    setBusy(onBusyChange, "next");
    if (typeof handleButtonClick === "function") {
      handleButtonClick("sectionB");
    }

    var deletes = (deletedIdsRef.current || []).map(function (delId) {
      return NewJoineeAPI.deleteEmploymentHistory(delId).catch(function () {
        return null;
      });
    });

    Promise.all(deletes)
      .then(function () {
        return Promise.all(
          rows.map(function (row) {
            if (row._id) {
              return NewJoineeAPI.updateEmploymenthistory(row._id, row);
            }
            return NewJoineeAPI.createEmploymentHistory(row);
          })
        );
      })
      .then(function () {
        deletedIdsRef.current = [];
        var after = isAdmin ? Promise.resolve(true) : deleteDraftB();
        return after.then(function () {
          setBusy(onBusyChange, false);
          if (formikHelpers && formikHelpers.setSubmitting) {
            formikHelpers.setSubmitting(false);
          }
          toast.success("Employment history saved");
          if (typeof changeTab === "function") {
            changeTab(2);
          }
        });
      })
      .catch(function (err) {
        console.log(err);
        setBusy(onBusyChange, false);
        if (formikHelpers && formikHelpers.setSubmitting) {
          formikHelpers.setSubmitting(false);
        }
        toast.error("Unable to save employment history");
      });
  }

  useImperativeHandle(
    formikRef,
    function () {
      return {
        submitDraft: function () {
          var formik = formikInstanceRef.current;
          if (!formik || readOnly) {
            return Promise.resolve();
          }
          return handleSaveDraft(formik);
        },
        submitNext: function () {
          var formik = formikInstanceRef.current;
          if (!formik || readOnly) {
            return Promise.resolve();
          }
          return formik.validateForm().then(function (errors) {
            notifyFormInvalid(errors);
            return formik.submitForm();
          });
        },
        getFormik: function () {
          return formikInstanceRef.current;
        },
      };
    },
    [userId, isAdmin, onBusyChange, changeTab, handleButtonClick, readOnly]
  );

  if (loading) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography
          sx={{
            fontFamily: JOINEE_FORM_FONT,
            fontSize: 14,
            fontWeight: 400,
            color: JOINEE_FORM_COLORS.placeholder,
          }}
        >
          Loading employment history…
        </Typography>
      </Box>
    );
  }

  if (!summary.hasPriorExperience) {
    return (
      <Box sx={{ py: 3, display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography
          sx={{
            fontFamily: JOINEE_FORM_FONT,
            fontSize: 14,
            fontWeight: 400,
            color: "#848F9A",
          }}
        >
          Candidate Info shows no prior experience. This step is not required.
        </Typography>
        <Button
          type="button"
          variant="contained"
          onClick={function () {
            if (typeof handleButtonClick === "function") {
              handleButtonClick("sectionB");
            }
            if (typeof changeTab === "function") {
              changeTab(2);
            }
          }}
          sx={{
            alignSelf: "flex-start",
            fontFamily: JOINEE_FORM_FONT,
            textTransform: "none",
            fontWeight: 400,
            bgcolor: JOINEE_FORM_COLORS.primary,
          }}
        >
          Continue to Education
        </Button>
      </Box>
    );
  }

  return (
    <Formik
      key={formKey}
      initialValues={initialValues}
      enableReinitialize
      validationSchema={buildValidationSchema()}
      onSubmit={handleSaveAndNext}
    >
      {function (formik) {
        formikInstanceRef.current = formik;
        var values = formik.values;

        function onFieldChange(e) {
          formik.handleChange(e);
        }

        return (
          <Form noValidate>
            <Box
              component="fieldset"
              disabled={readOnly}
              sx={{
                border: 0,
                m: 0,
                p: 0,
                minWidth: 0,
                pointerEvents: readOnly ? "none" : "auto",
              }}
            >
            <FormSectionTitle>Do you have prior experience?</FormSectionTitle>
            <FormTwoCol>
              <ReadOnlyBox
                label="Prior experience"
                value={summary.hasPriorExperience ? "Yes" : "No"}
              />
              <Box />
            </FormTwoCol>
            <FormTwoCol>
              <ReadOnlyBox
                label="Total Experience"
                value={formatExperienceLabel(
                  summary.totalExperienceInYears,
                  summary.totalExperienceInMonths
                )}
              />
              <ReadOnlyBox
                label="Relevant Experience"
                value={formatExperienceLabel(
                  summary.relevantExperienceInYears,
                  summary.relevantExperienceInMonths
                )}
              />
            </FormTwoCol>

            <FieldArray name="employmentHistory">
              {function (arrayHelpers) {
                return (
                  <Box>
                    {(values.employmentHistory || []).map(function (emp, index) {
                      var base = "employmentHistory[" + index + "]";
                      return (
                        <Box key={"emp-" + index} sx={{ mb: 4 }}>
                          <FormSectionTitle
                            action={
                              <Box sx={{ display: "flex", gap: 1 }}>
                                {index === 0 ? (
                                  <FormAddButton
                                    onClick={function () {
                                      arrayHelpers.push(
                                        emptyEmployerRow(userId)
                                      );
                                    }}
                                  >
                                    + Add Employment
                                  </FormAddButton>
                                ) : null}
                                {(values.employmentHistory || []).length > 1 ? (
                                  <Button
                                    type="button"
                                    variant="outlined"
                                    onClick={function () {
                                      var row = values.employmentHistory[index];
                                      if (row && row._id) {
                                        deletedIdsRef.current =
                                          deletedIdsRef.current.concat([
                                            row._id,
                                          ]);
                                      }
                                      arrayHelpers.remove(index);
                                    }}
                                    sx={{
                                      fontFamily: JOINEE_FORM_FONT,
                                      fontSize: 14,
                                      fontWeight: 400,
                                      textTransform: "none",
                                      color: JOINEE_FORM_COLORS.error,
                                      borderColor: JOINEE_FORM_COLORS.border,
                                      borderRadius: "6px",
                                      minHeight: 32,
                                    }}
                                  >
                                    Remove
                                  </Button>
                                ) : null}
                              </Box>
                            }
                          >
                            Employer History {index + 1}
                          </FormSectionTitle>

                          <FormTwoCol>
                            <FormTextField
                              id={"eh-employer-" + index}
                              name={base + ".employerName"}
                              rule="orgName"
                              label="Name of The Employer/Organization"
                              placeholder="Enter the Name of Employer/Organization"
                              required
                              value={emp.employerName}
                              onChange={onFieldChange}
                              onBlur={formik.handleBlur}
                              error={errMsg(formik, base + ".employerName")}
                            />
                            <FormTextField
                              id={"eh-phone-" + index}
                              name={base + ".companyContact"}
                              rule="mobile"
                              label="Phone Number"
                              placeholder="Enter Number"
                              required
                              value={emp.companyContact}
                              onChange={onFieldChange}
                              onBlur={formik.handleBlur}
                              error={errMsg(formik, base + ".companyContact")}
                              inputProps={{ maxLength: 10 }}
                            />
                          </FormTwoCol>

                          <FormTwoCol>
                            <FormTextField
                              id={"eh-dept-" + index}
                              name={base + ".department"}
                              rule="text"
                              label="Department"
                              placeholder="Enter Department"
                              required
                              value={emp.department}
                              onChange={onFieldChange}
                              onBlur={formik.handleBlur}
                              error={errMsg(formik, base + ".department")}
                            />
                            <FormTextField
                              id={"eh-code-" + index}
                              name={base + ".employeeCode"}
                              rule="code"
                              label="Employee Code"
                              placeholder="Enter Employee Code"
                              required
                              value={emp.employeeCode}
                              onChange={onFieldChange}
                              onBlur={formik.handleBlur}
                              error={errMsg(formik, base + ".employeeCode")}
                            />
                          </FormTwoCol>

                          <FormTwoCol>
                            <FormTextField
                              id={"eh-desig-" + index}
                              name={base + ".designation"}
                              rule="text"
                              label="Designation"
                              placeholder="Enter Designation"
                              required
                              value={emp.designation}
                              onChange={onFieldChange}
                              onBlur={formik.handleBlur}
                              error={errMsg(formik, base + ".designation")}
                            />
                            <FormTextField
                              id={"eh-rem-" + index}
                              name={base + ".remuneration"}
                              rule="remuneration"
                              label="Remuneration(in Lakhs)"
                              placeholder="e.g. 12.50"
                              required
                              value={emp.remuneration}
                              onChange={onFieldChange}
                              onBlur={formik.handleBlur}
                              error={errMsg(formik, base + ".remuneration")}
                            />
                          </FormTwoCol>

                          <FormTwoCol>
                            <FormTextField
                              id={"eh-reported-" + index}
                              name={base + ".reportedTo"}
                              rule="personName"
                              label="Reported To"
                              placeholder="Enter Report person name"
                              required
                              value={emp.reportedTo}
                              onChange={onFieldChange}
                              onBlur={formik.handleBlur}
                              error={errMsg(formik, base + ".reportedTo")}
                            />
                            <FormTextField
                              id={"eh-reported-desig-" + index}
                              name={base + ".reportedPersonDesig"}
                              rule="text"
                              label="Reported Person Designation"
                              placeholder="Enter Reported Person Designation"
                              required
                              value={emp.reportedPersonDesig}
                              onChange={onFieldChange}
                              onBlur={formik.handleBlur}
                              error={errMsg(
                                formik,
                                base + ".reportedPersonDesig"
                              )}
                            />
                          </FormTwoCol>

                          <FormTwoCol>
                            <FormSelectField
                              id={"eh-nature-" + index}
                              name={base + ".natureOfBusiness"}
                              label="Nature of Business"
                              placeholder="Select Nature of Business"
                              required
                              options={NATURE_OF_BUSINESS_OPTIONS}
                              value={emp.natureOfBusiness}
                              onChange={onFieldChange}
                              onBlur={formik.handleBlur}
                              error={errMsg(formik, base + ".natureOfBusiness")}
                            />
                            <FormSelectField
                              id={"eh-type-" + index}
                              name={base + ".typeOfEmployment"}
                              label="Type of Employment"
                              placeholder="Select Type of Employment"
                              required
                              options={TYPE_OF_EMPLOYMENT_OPTIONS}
                              value={emp.typeOfEmployment}
                              onChange={onFieldChange}
                              onBlur={formik.handleBlur}
                              error={errMsg(formik, base + ".typeOfEmployment")}
                            />
                          </FormTwoCol>

                          <FormTwoCol>
                            <FormTextField
                              id={"eh-addr1-" + index}
                              name={base + ".addressLine1"}
                              rule="address"
                              label="Address Line 1"
                              placeholder="Enter Address Line 1"
                              required
                              value={emp.addressLine1}
                              onChange={onFieldChange}
                              onBlur={formik.handleBlur}
                              error={errMsg(formik, base + ".addressLine1")}
                            />
                            <FormTextField
                              id={"eh-addr2-" + index}
                              name={base + ".addressLine2"}
                              rule="address"
                              label="Address Line 2"
                              placeholder="Enter Address Line 2"
                              value={emp.addressLine2}
                              onChange={onFieldChange}
                              onBlur={formik.handleBlur}
                              error={errMsg(formik, base + ".addressLine2")}
                            />
                          </FormTwoCol>

                          <FormTwoCol>
                            <FormTextField
                              id={"eh-city-" + index}
                              name={base + ".city"}
                              rule="place"
                              label="City"
                              placeholder="Enter City"
                              required
                              value={emp.city}
                              onChange={onFieldChange}
                              onBlur={formik.handleBlur}
                              error={errMsg(formik, base + ".city")}
                            />
                            <FormTextField
                              id={"eh-state-" + index}
                              name={base + ".state"}
                              rule="place"
                              label="State"
                              placeholder="Enter State"
                              required
                              value={emp.state}
                              onChange={onFieldChange}
                              onBlur={formik.handleBlur}
                              error={errMsg(formik, base + ".state")}
                            />
                          </FormTwoCol>

                          <FormTwoCol>
                            <FormDateField
                              id={"eh-doj-" + index}
                              name={base + ".doj"}
                              label="Date of Joining"
                              placeholder="Select Date of Joining"
                              required
                              disableFuture
                              value={emp.doj}
                              onChange={function (iso) {
                                formik.setFieldValue(base + ".doj", iso);
                              }}
                              onBlur={formik.handleBlur}
                              error={errMsg(formik, base + ".doj")}
                            />
                            <FormDateField
                              id={"eh-dol-" + index}
                              name={base + ".dol"}
                              label="Date of Leaving"
                              placeholder="Select Date of Leaving"
                              required
                              minDate={emp.doj || undefined}
                              value={emp.dol}
                              onChange={function (iso) {
                                formik.setFieldValue(base + ".dol", iso);
                              }}
                              onBlur={formik.handleBlur}
                              error={errMsg(formik, base + ".dol")}
                            />
                          </FormTwoCol>

                          <Box sx={{ width: { xs: "100%", md: "50%" }, mb: 1 }}>
                            <FormTextField
                              id={"eh-reason-" + index}
                              name={base + ".reasonForLeaving"}
                              label="Reason for leaving"
                              placeholder="Enter Reason for leaving"
                              required
                              multiline
                              rows={3}
                              inputProps={{ maxLength: 250 }}
                              value={emp.reasonForLeaving}
                              onChange={onFieldChange}
                              onBlur={formik.handleBlur}
                              error={errMsg(formik, base + ".reasonForLeaving")}
                            />
                          </Box>

                          <FormSectionTitle>
                            Reference Contact Person
                          </FormSectionTitle>
                          <FormTwoCol>
                            <FormTextField
                              id={"eh-ref1-name-" + index}
                              name={base + ".referenceContactPerson1Name"}
                              rule="personName"
                              label="Name"
                              placeholder="Enter Full Name"
                              required
                              value={emp.referenceContactPerson1Name}
                              onChange={onFieldChange}
                              onBlur={formik.handleBlur}
                              error={errMsg(
                                formik,
                                base + ".referenceContactPerson1Name"
                              )}
                            />
                            <FormTextField
                              id={"eh-ref1-phone-" + index}
                              name={base + ".referenceContactPerson1Contact"}
                              rule="mobile"
                              label="Phone Number"
                              required
                              value={emp.referenceContactPerson1Contact}
                              onChange={onFieldChange}
                              onBlur={formik.handleBlur}
                              error={errMsg(
                                formik,
                                base + ".referenceContactPerson1Contact"
                              )}
                              inputProps={{ maxLength: 10 }}
                            />
                          </FormTwoCol>
                          <FormTwoCol>
                            <FormTextField
                              id={"eh-ref2-name-" + index}
                              name={base + ".referenceContactPerson2Name"}
                              rule="personName"
                              label="Name"
                              placeholder="Enter Full Name"
                              required
                              value={emp.referenceContactPerson2Name}
                              onChange={onFieldChange}
                              onBlur={formik.handleBlur}
                              error={errMsg(
                                formik,
                                base + ".referenceContactPerson2Name"
                              )}
                            />
                            <FormTextField
                              id={"eh-ref2-phone-" + index}
                              name={base + ".referenceContactPerson2Contact"}
                              rule="mobile"
                              label="Phone Number"
                              required
                              value={emp.referenceContactPerson2Contact}
                              onChange={onFieldChange}
                              onBlur={formik.handleBlur}
                              error={errMsg(
                                formik,
                                base + ".referenceContactPerson2Contact"
                              )}
                              inputProps={{ maxLength: 10 }}
                            />
                          </FormTwoCol>
                        </Box>
                      );
                    })}
                  </Box>
                );
              }}
            </FieldArray>

            </Box>

            {!hideInternalActions ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1.5,
                  mt: 2,
                }}
              >
                <Button
                  type="button"
                  variant="outlined"
                  onClick={function () {
                    handleSaveDraft(formik);
                  }}
                  sx={{
                    fontFamily: JOINEE_FORM_FONT,
                    textTransform: "none",
                    fontWeight: 400,
                  }}
                >
                  Save as Draft
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    fontFamily: JOINEE_FORM_FONT,
                    textTransform: "none",
                    fontWeight: 400,
                    bgcolor: JOINEE_FORM_COLORS.primary,
                  }}
                >
                  Save and next
                </Button>
              </Box>
            ) : null}
          </Form>
        );
      }}
    </Formik>
  );
}

export default EmploymentHistory;
